import * as React from 'react'
import { Link } from 'react-router'
import { AlertTriangle, Check, Loader2, RefreshCw } from 'lucide-react'
import type { HousecallProStatusDto } from '@lib/dto/housecall-pro.dto'
import {
  IRRIGATION_PART_KINDS,
  IRRIGATION_PART_KIND_LABELS,
  type IrrigationPartKind,
} from '@lib/enums/irrigation-part-kind.enum'
import type { UpdateHousecallProInput } from '@lib/input/housecall-pro.input'
import {
  useHcpTopCategories,
  useHousecallProStatus,
  useSyncHousecallPro,
  useUpdateHousecallPro,
} from '@/hooks/useHousecallPro'
import { Button } from '@/components/ui/button'
import { SELECT_CLASS } from '@/components/ui/field-classes'
import { errorMessage } from '@/helpers/logger'

/** "Oct 8, 2026, 12:40 PM", or a plain "never". */
function formatSyncedAt(syncedAt: string | null): string {
  if (syncedAt === null) return 'never'
  return new Date(syncedAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

type PartCategoryDraft = Record<IrrigationPartKind, string | null>

/** Every picker, unset unless the server says otherwise. */
function toPartCategoryDraft(status: HousecallProStatusDto): PartCategoryDraft {
  const result: PartCategoryDraft = {
    TIMER: null,
    VALVE: null,
    BALL_VALVE: null,
    BACKFLOW: null,
    SENSOR: null,
    DECODER: null,
    WELL_COMPONENT: null,
    VALVE_BOX: null,
  }
  for (const entry of status.partCategories) {
    result[entry.kind] = entry.categoryUuid
  }
  return result
}

function SectionFrame({ children }: { children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">Parts from Housecall Pro</h2>
        <p className="mt-0.5 text-sm text-muted-text">
          The irrigation survey picks parts from the price book in Housecall
          Pro. Nothing here changes the price book.
        </p>
      </div>
      {children}
    </section>
  )
}

/**
 * Where irrigation parts come from: the Housecall Pro category they are filed
 * in, which subcategory each part picker reads, and how fresh the copy is.
 *
 * A deployment with no key gets a sentence saying so rather than a box to
 * paste one into. The key can change the live account, so it is
 * configuration that somebody sets deliberately, not a form field.
 */
export function HousecallProSettings() {
  const status = useHousecallProStatus()
  // Held here, above the key: a save remounts the form, and "Saved" has to
  // outlive that.
  const [saved, setSaved] = React.useState(false)

  if (status.isPending) {
    return (
      <SectionFrame>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
        </div>
      </SectionFrame>
    )
  }

  if (status.isError) {
    return (
      <SectionFrame>
        <p className="text-sm text-danger">
          The connection could not be loaded. Check your connection and try
          again.
        </p>
      </SectionFrame>
    )
  }

  if (!status.data.configured) {
    return (
      <SectionFrame>
        <p className="rounded-md border border-default bg-subtle px-3 py-2 text-sm text-secondary-text">
          Housecall Pro is not connected on this deployment. Whoever runs it
          sets <code className="text-xs">HOUSECALL_PRO_API_KEY</code> on the
          API. Until then the part pickers have nothing to offer.
        </p>
      </SectionFrame>
    )
  }

  return (
    // Keyed on what was last saved, so a save or a sync resets the draft to
    // what the server now says rather than leaving a stale edit on screen.
    <ConnectedSettings
      key={`${status.data.irrigationCategory?.uuid ?? ''}:${status.data.syncedAt ?? ''}:${JSON.stringify(status.data.partCategories)}`}
      status={status.data}
      saved={saved}
      onSavedChange={setSaved}
    />
  )
}

function ConnectedSettings({
  status,
  saved,
  onSavedChange,
}: {
  status: HousecallProStatusDto
  /** Whether the last save went through and nothing has changed since. */
  saved: boolean
  onSavedChange: (saved: boolean) => void
}) {
  const topCategories = useHcpTopCategories(true)
  const update = useUpdateHousecallPro()
  const sync = useSyncHousecallPro()
  const [error, setError] = React.useState<string | null>(null)
  const savedRoot = status.irrigationCategory?.uuid ?? null
  const [root, setRoot] = React.useState<string | null>(savedRoot)
  const [partCategories, setPartCategories] = React.useState<PartCategoryDraft>(
    () => toPartCategoryDraft(status),
  )
  const savedPartCategories = React.useMemo(
    () => toPartCategoryDraft(status),
    [status],
  )

  const rootChanged = root !== savedRoot
  const pickersChanged = IRRIGATION_PART_KINDS.some(
    (kind) => partCategories[kind] !== savedPartCategories[kind],
  )
  const isDirty = rootChanged || pickersChanged
  const isBusy = update.isPending || sync.isPending

  async function handleSave(): Promise<void> {
    setError(null)
    onSavedChange(false)
    // A new category replaces the subcategories the pickers point into, so
    // the two are never saved together: pick the category, then the pickers.
    const input: UpdateHousecallProInput = rootChanged
      ? { irrigationCategoryUuid: root }
      : {
          partCategories: IRRIGATION_PART_KINDS.map((kind) => ({
            kind,
            categoryUuid: partCategories[kind],
          })),
        }
    try {
      await update.mutateAsync(input)
      onSavedChange(true)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  async function handleSync(): Promise<void> {
    setError(null)
    try {
      await sync.mutateAsync()
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  function renderRootOptions(): React.ReactNode {
    const options = topCategories.data?.categories ?? []
    // Until the live list arrives, the saved choice still shows by name.
    const known = options.some((option) => option.uuid === savedRoot)
    return (
      <>
        <option value="">Choose a category</option>
        {!known && status.irrigationCategory !== null ? (
          <option value={status.irrigationCategory.uuid}>
            {status.irrigationCategory.name}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.name}
          </option>
        ))}
      </>
    )
  }

  function renderPickers(): React.ReactNode {
    if (rootChanged) {
      return (
        <p className="text-sm text-muted-text">
          Save to read this category from Housecall Pro. Then choose which
          subcategory each picker reads.
        </p>
      )
    }
    if (savedRoot === null) {
      return null
    }
    if (status.subcategories.length === 0) {
      return (
        <p className="rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
          {status.irrigationCategory?.name ?? 'This category'} has no
          subcategories yet. Until its materials are filed into some in
          Housecall Pro (Valves, Timers, Backflow…), every picker offers every
          irrigation material.
        </p>
      )
    }
    return (
      <div>
        <p className="mb-1.5 text-sm font-medium">Part pickers</p>
        <div className="divide-y divide-default rounded-md border border-default">
          {IRRIGATION_PART_KINDS.map((kind) => (
            <div
              key={kind}
              className="grid grid-cols-[10rem_1fr] items-center gap-3 px-3 py-1.5"
            >
              <label htmlFor={`part-category-${kind}`} className="text-sm">
                {IRRIGATION_PART_KIND_LABELS[kind]}
              </label>
              <select
                id={`part-category-${kind}`}
                className={SELECT_CLASS}
                value={partCategories[kind] ?? ''}
                disabled={isBusy}
                onChange={(event) => {
                  onSavedChange(false)
                  setPartCategories({
                    ...partCategories,
                    [kind]:
                      event.target.value === '' ? null : event.target.value,
                  })
                }}
              >
                <option value="">Every irrigation material</option>
                {status.subcategories.map((subcategory) => (
                  <option key={subcategory.uuid} value={subcategory.uuid}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <SectionFrame>
      {/*
        Said here because the client page offers "Create it in Housecall Pro"
        only where it is on, and somebody wondering why it is missing looks
        in Settings first.
      */}
      <p className="text-xs text-muted-text">
        {status.writesEnabled
          ? 'This app can create customers and addresses in Housecall Pro from a client’s page, and jobs from an accepted proposal’s.'
          : 'This app only reads from Housecall Pro: clients are linked to customers created there.'}{' '}
        Switched on and off under{' '}
        <Link
          to="/settings?section=business"
          className="underline underline-offset-2"
        >
          Business
        </Link>
        .
      </p>
      <div className="space-y-1">
        <label
          htmlFor="hcp-irrigation-category"
          className="text-sm font-medium"
        >
          Irrigation materials category
        </label>
        <select
          id="hcp-irrigation-category"
          className={SELECT_CLASS}
          value={root ?? ''}
          disabled={isBusy}
          onChange={(event) => {
            onSavedChange(false)
            setRoot(event.target.value === '' ? null : event.target.value)
          }}
        >
          {renderRootOptions()}
        </select>
        {topCategories.isError ? (
          <p className="text-xs text-danger">
            {errorMessage(topCategories.error, 'That could not be saved.')}
          </p>
        ) : (
          <p className="text-xs text-muted-text">
            The category in Housecall Pro irrigation parts are filed in, with
            every subcategory under it.
          </p>
        )}
      </div>

      {savedRoot === null ? null : (
        <div className="flex items-center justify-between gap-3 rounded-md border border-default px-3 py-2">
          <p className="text-sm text-secondary-text">
            {status.materialCount} materials · read{' '}
            {formatSyncedAt(status.syncedAt)}
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={isBusy}
            onClick={() => {
              void handleSync()
            }}
          >
            {sync.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Sync now
          </Button>
        </div>
      )}

      {status.skippedMaterials.length === 0 ? null : (
        <p className="flex gap-2 rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
          <span>
            Housecall Pro sent {status.skippedMaterials.length} material
            {status.skippedMaterials.length === 1 ? '' : 's'} this app could not
            read, so{' '}
            {status.skippedMaterials.length === 1 ? 'it is' : 'they are'} left
            out: {status.skippedMaterials.join(', ')}. Fix{' '}
            {status.skippedMaterials.length === 1 ? 'it' : 'them'} in Housecall
            Pro and sync again.
          </span>
        </p>
      )}

      {renderPickers()}

      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      <div className="flex items-center gap-3 border-t border-default pt-3">
        <Button
          disabled={!isDirty || isBusy}
          onClick={() => {
            void handleSave()
          }}
        >
          {update.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          Save changes
        </Button>
        {saved && !isDirty ? (
          <span className="flex items-center gap-1 text-sm text-muted-text">
            <Check className="h-3.5 w-3.5" />
            Saved
          </span>
        ) : null}
      </div>
    </SectionFrame>
  )
}
